<template lang="pug">
.teaser-container.page-main.visible-grid(v-if="config")
    .page-area
        .sticky-container
            h4.title.area-title-bar.sticky {{ $t('Basic Information') }}
            .sections
                .teaser.droppable
                    .left
                        image-content(
                            v-if="section && imageContent && imageContent.image && imageContent.image.min === 1 && imageContent.image.max === 1"
                            :section="section"
                            :config="imageContent"
                            :options="options"
                            :edit="true"
                            :height="{ preview: 80, edit: 154 }"
                        )
                        media-content(
                            v-if="section && videoContent && videoContent.media && videoContent.media.min === 1 && videoContent.media.max === 1"
                            :dropbox="false"
                            :section="section"
                            :config="videoContent"
                            :options="options"
                            :edit="true"
                            :height="{ preview: 80, edit: 154 }"
                        )
                    .right
                        slot
                        .content-wrappers(v-if="section && otherContents && otherContents.length")
                            .content-wrapper(
                                v-for="config in otherContents"
                                :key="config.key"
                            )
                                el-collapse.section-collapse(
                                    v-if="config.key === 'section'"
                                    v-model="activeCollapseItem"
                                    accordion
                                )
                                    el-collapse-item(:name="config.key")
                                        template(slot="title")
                                            | {{ sectionCollapseTitle(config) }}
                                        input-contents(
                                            :section="section"
                                            :config="config"
                                            :options="options"
                                            :edit="true"
                                            :hide-add-button="true"
                                            :auto-add-first="true"
                                        )
                                image-content(
                                    v-else-if="config.image && config.image.min === 1 && config.image.max === 1"
                                    :section="section"
                                    :config="config"
                                    :options="options"
                                    :edit="true"
                                )
                                input-contents(
                                    v-else
                                    :section="section"
                                    :config="config"
                                    :options="options"
                                    :edit="true"
                                )
    .mb-8
    //- hr.wave
</template>

<i18n>
{
    "en": {
        "Section": "Section",
        "Heading": "Heading",
        "Content": "Content",
        "Hyperlink (optional)": "Hyperlink (optional)"
    },
    "de": {
        "Teaser": "Anreißer",
        "Basic Information": "Grundeinstellung",
        "Section": "Sektion",
        "Heading": "Überschrift",
        "Content": "Inhalt",
        "Hyperlink (optional)": "Hyperlink (optional)"
    }
}
</i18n>

<script>

import _ from 'lodash'
import { useSetupStore, usePageStore } from '@/stores'
import { addContentToSection } from '@/helpers'

import ImageContent from '@/components/Page/Sections/Partials/ImageContent'
import MediaContent from '@/components/Page/Sections/Partials/MediaContent'
import InputContents from '@/components/Page/Sections/Partials/InputContents'

export default {
    name: 'Teaser',
    data: () => ({
        section: undefined,
        activeCollapseItem: '',
    }),
    watch: {
        teaser(teaser) {
            // initialize teaser async after fetch
            if (!this.section) this.section = _.cloneDeep(teaser)
            this.ensureSectionContent()
        },
        config() {
            this.ensureSectionContent()
        },
        section: {
            deep: true,
            handler: _.debounce(function(newSection, oldSection) {
                if (!oldSection) oldSection = newSection
                this.replaceSection({ oldSection, newSection })
            }, 100)
        },
    },
    components: {
        ImageContent,
        MediaContent,
        InputContents,
    },
    computed: {
        pageStore() {
            return usePageStore()
        },
        form() {
            return this.pageStore.form
        },
        options() {
            return this.pageStore.options
        },
        teaser() {
            const sections = _.get(this.form, 'sections') || []
            const section = sections.find(section => section.key === 'category-page-teaser')
            if (section) return section
        },
        config() {
            return _.get(this.activeCms, 'config.template.teaser')
        },
        teaserContents() {
            const contents = _.cloneDeep(_.get(this.config, 'contents', []))
                .map(content => {
                    if (content.key !== 'section') return content

                    return {
                        ...content,
                        min: 1,
                        max: 1,
                        initialize: true
                    }
                })
            const hasSectionContent = contents.some(content => content.key === 'section')

            // Fallback so backend-ui can edit the teaser section even if the
            // loaded template config is still missing the "section" block.
            if (!hasSectionContent) {
                contents.push({
                    key: 'section',
                    name: this.$t('Section'),
                    min: 1,
                    max: 1,
                    initialize: true,
                    fields: [
                        {
                            key: 'title',
                            name: this.$t('Heading'),
                            type: 'textfield'
                        },
                        {
                            key: 'text',
                            name: this.$t('Content'),
                            type: 'rte'
                        },
                        {
                            key: 'hyperlink',
                            name: this.$t('Hyperlink (optional)'),
                            type: 'linkfield'
                        }
                    ]
                })
            }

            return contents
        },
        imageContent() {
            return this.teaserContents
                .find(content => content.key === 'image')
        },
        videoContent() {
            return this.teaserContents
                .find(content => content.key === 'video')
        },
        otherContents() {
            return this.teaserContents
                .filter(content => ['image', 'video'].includes(content.key) === false)
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
    },
    methods: {
        replaceSection(payload) {
            this.pageStore.replaceSection(payload)
        },
        ensureSectionContent() {
            if (!this.section) return

            const sectionConfig = this.teaserContents.find(content => content.key === 'section')
            if (!sectionConfig) return

            const sectionContents = _.get(this.section, 'contents', [])
                .filter(content => content && content.key === 'section' && content.deleted !== true)

            if (!sectionContents.length) {
                addContentToSection(this.section, sectionConfig)
            }
        },
        sectionCollapseTitle(config) {
            const defaultTitle = config.name || this.$t('Section')
            if (!this.section || !this.section.contents || !this.section.contents.length) return defaultTitle

            const content = this.section.contents.find(content => content && content.key === config.key)
            const customTitle = _.get(content, 'title')
            if (_.isString(customTitle) && customTitle.trim().length) return customTitle.trim()

            return defaultTitle
        },
    },
    mounted() {
        this.section = _.cloneDeep(this.teaser)
        this.ensureSectionContent()
    },
}
</script>

<style lang="sass" scoped>
.teaser-container
    --nesting-level: 1
    --title-top: 0

.teaser
    display: flex
    flex-direction: row
    .left
        flex: 0 0 280px
        max-width: 280px
        &::v-deep
            .media,
            .images
                margin-right: 16px
                ul.no-style-list
                    margin: 0
                    li.content
                        margin: 0
            .plus-box .el-upload .el-upload-dragger
                height: 155px

    .right
        flex-grow: 1
        min-width: 0
        &::v-deep
            .title-bar
                display: none !important
            .section-collapse
                background-color: #eeeff2 !important
                padding: 0px 5px
                border-radius: 5px
                border-top: 0
                border-bottom: 0
                .el-collapse-item__header,
                .el-collapse-item__wrap,
                .el-collapse-item__content
                    background-color: #eeeff2 !important
                .el-collapse-item__header
                    padding: 0 8px
                .el-collapse-item__content
                    padding: 8px 8px 10px
</style>

